/* ClutchCloth homepage. Native links/content work without JavaScript. */
(() => {
  if (window.ccHomeInitialized) return;
  window.ccHomeInitialized = true;
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const parallaxQuery = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  let paused = motionQuery.matches;
  let raf = 0;
  const controllers = new Map();
  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('cc-visible');
      revealObserver.unobserve(entry.target);
    });
  }, { threshold:0.08 }) : null;

  function updateMotion() {
    document.body.classList.toggle('cc-motion-paused', paused || motionQuery.matches);
    document.querySelectorAll('[data-cc-motion]').forEach(button => {
      button.hidden = false;
      button.disabled = motionQuery.matches;
      button.setAttribute('aria-pressed', String(paused || motionQuery.matches));
      button.querySelector('[data-cc-motion-label]').textContent = motionQuery.matches ? 'Reduced motion' : (paused ? 'Resume motion' : 'Pause motion');
      button.setAttribute('aria-label', motionQuery.matches ? 'Reduced motion enabled by your device' : (paused ? 'Resume slideshow and decorative motion' : 'Pause slideshow and decorative motion'));
      const icon = button.querySelector('[data-cc-motion-icon]');
      if (icon) icon.textContent = paused ? '▷' : 'Ⅱ';
    });
    controllers.forEach(controller => {
      controller.schedule();
      if (paused || motionQuery.matches) controller.media?.style.removeProperty('transform');
    });
  }

  function createController(hero) {
    const scenes = Array.from(hero.querySelectorAll('[data-cc-scene]'));
    const abort = new AbortController();
    const signal = abort.signal;
    const interval = Math.max(5, Math.min(15, Number(hero.dataset.ccInterval) || 8)) * 1000;
    const failed = new Set();
    let index = 0;
    let timer = 0;
    let request = 0;
    let hovered = false;
    let visible = true;
    let destroyed = false;
    const eligible = () => !destroyed && !paused && !motionQuery.matches && !document.hidden && !hovered && visible && hero.dataset.ccAutoplay === 'true' && scenes.length > 1;
    function schedule() {
      clearTimeout(timer);
      timer = 0;
      if (eligible()) timer = setTimeout(() => { void show(nextIndex(1), false); }, interval);
    }
    function nextIndex(direction) {
      let next = index;
      for (let i = 0; i < scenes.length; i++) {
        next = (next + direction + scenes.length) % scenes.length;
        if (!failed.has(next)) return next;
      }
      return index;
    }
    async function show(next, manual) {
      clearTimeout(timer);
      if (next < 0 || next >= scenes.length || next === index) { schedule(); return; }
      const token = ++request;
      const img = scenes[next].querySelector('img');
      try {
        if (img) {
          img.loading = 'eager';
          if (img.decode) await img.decode();
          else if (!img.complete) await new Promise((resolve, reject) => {
            img.addEventListener('load', resolve, { once:true, signal });
            img.addEventListener('error', reject, { once:true, signal });
          });
          if (!img.naturalWidth) throw new Error('Hero image unavailable');
        }
      } catch {
        if (token === request && !destroyed) { failed.add(next); schedule(); }
        return;
      }
      if (destroyed || token !== request || (!manual && !eligible())) return;
      index = next;
      const id = scenes[index].dataset.ccScene;
      scenes.forEach((scene, i) => scene.classList.toggle('is-active', i === index));
      hero.querySelectorAll('[data-cc-headline], [data-cc-copy]').forEach(element => {
        const active = (element.dataset.ccHeadline || element.dataset.ccCopy) === id;
        element.classList.toggle('is-active', active);
        element.setAttribute('aria-hidden', String(!active));
      });
      hero.querySelectorAll('[data-cc-select]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.ccSelect === id)));
      if (manual) {
        const status = hero.querySelector('[data-cc-slide-status]');
        const label = hero.querySelector(`[data-cc-select="${id}"]`)?.getAttribute('aria-label') || '';
        if (status) status.textContent = `Slide ${index + 1} of ${scenes.length}: ${label}`;
      }
      schedule();
    }
    hero.querySelectorAll('[data-cc-controls]').forEach(control => { control.hidden = false; });
    hero.addEventListener('click', event => {
      const button = event.target instanceof Element ? event.target.closest('button') : null;
      if (!button) return;
      let next;
      if (button.hasAttribute('data-cc-next')) next = nextIndex(1);
      else if (button.hasAttribute('data-cc-previous')) next = nextIndex(-1);
      else if (button.hasAttribute('data-cc-select')) next = scenes.findIndex(scene => scene.dataset.ccScene === button.dataset.ccSelect);
      else return;
      paused = true; // Manual navigation stays paused until explicitly resumed.
      updateMotion();
      void show(next, true);
    }, { signal });
    hero.addEventListener('mouseenter', () => { hovered = true; schedule(); }, { signal });
    hero.addEventListener('mouseleave', () => { hovered = false; schedule(); }, { signal });
    hero.addEventListener('focusin', () => { paused = true; updateMotion(); }, { signal });
    const visibilityObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      schedule();
    }, { threshold:0 }) : null;
    visibilityObserver?.observe(hero);
    return {
      media:hero.querySelector('[data-cc-parallax]'),
      schedule,
      destroy() { destroyed = true; request++; clearTimeout(timer); abort.abort(); visibilityObserver?.disconnect(); }
    };
  }
  function initialize(root = document) {
    root.querySelectorAll('[data-cc-marquee]').forEach(banner => banner.classList.add('is-ready'));
    root.querySelectorAll('[data-cc-reveal]').forEach(el => revealObserver?.observe(el));
    root.querySelectorAll('[data-cc-hero]').forEach(hero => {
      if (!controllers.has(hero)) controllers.set(hero, createController(hero));
    });
    updateMotion();
  }
  function updateParallax() {
    raf = 0;
    if (paused || motionQuery.matches || !parallaxQuery.matches) return;
    controllers.forEach((controller, hero) => {
      const rect = hero.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      if (controller.media) controller.media.style.transform = `translate3d(0,${Math.max(-25, Math.min(25, -rect.top * 0.055))}px,0)`;
    });
  }
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-cc-motion]') || motionQuery.matches) return;
    paused = !paused;
    updateMotion();
  });
  document.addEventListener('visibilitychange', () => controllers.forEach(controller => controller.schedule()));
  let parallaxTarget = null;
  const onParallaxScroll = () => {
    if (!raf && !paused && !motionQuery.matches) raf = requestAnimationFrame(updateParallax);
  };
  function bindParallax() {
    parallaxTarget?.removeEventListener('scroll', onParallaxScroll);
    parallaxTarget = null;
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    controllers.forEach(controller => controller.media?.style.removeProperty('transform'));
    if (parallaxQuery.matches && !motionQuery.matches) {
      parallaxTarget = document.querySelector('.page-wrapper') || window;
      parallaxTarget.addEventListener('scroll', onParallaxScroll, { passive:true });
    }
  }
  parallaxQuery.addEventListener('change', bindParallax);
  motionQuery.addEventListener('change', bindParallax);
  motionQuery.addEventListener('change', () => { paused = motionQuery.matches; updateMotion(); });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-cc-reveal]').forEach(el => revealObserver?.unobserve(el));
    controllers.forEach((controller, hero) => {
      if (event.target.contains(hero)) { controller.destroy(); controllers.delete(hero); }
    });
  });
  initialize();
  bindParallax();
})();
